import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import { setSnackbar } from './snackbarSlice';

export const getAllLicensesByOrgID = createAsyncThunk(
  'license/getAllByOrgId',
  async (orgId, { rejectWithValue }) => {
    try {
      const { data } = await API.get('/GetAllLicensesByOrgID', { params: { OrgId: orgId } });
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteLicense = createAsyncThunk(
  'license/delete',
  async ({ licenseId, orgId }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.delete('/deleteLicense', { params: { LicenseId: licenseId } });
      dispatch(getAllLicensesByOrgID(orgId));
      dispatch(setSnackbar({ status: 'success', message: 'License released successfully' }));
      return data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const deleteLicenses = createAsyncThunk(
  'license/deleteMany',
  async ({ licenseIds, orgId }, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        licenseIds.map((licenseId) =>
          API.delete('/deleteLicense', { params: { LicenseId: licenseId } })
        )
      );
      dispatch(getAllLicensesByOrgID(orgId));

      const failed = results.filter((r) => r.status === 'rejected').length;
      const succeeded = licenseIds.length - failed;
      if (failed === 0) {
        dispatch(setSnackbar({ status: 'success', message: `${succeeded} license${succeeded === 1 ? '' : 's'} released successfully` }));
      } else if (succeeded === 0) {
        dispatch(setSnackbar({ status: 'error', message: 'Failed to release the selected licenses' }));
      } else {
        dispatch(setSnackbar({ status: 'info', message: `Released ${succeeded} license${succeeded === 1 ? '' : 's'}, ${failed} failed` }));
      }
      return { succeeded, failed };
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const requestHelp = createAsyncThunk(
  'license/requestHelp',
  async (request, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.post('/zapierHelpRequest', { request });
      dispatch(setSnackbar({ status: 'success', message: 'Help request sent to our team successfully, you will receive an email once it is processed.' }));
      return data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

const licenseSlice = createSlice({
  name: 'license',
  initialState: {
    license: null,
    allLicenses: [],
    loading: false,
    error: null,
    message: null,
  },
  reducers: {
    clearLicenses: (state) => {
      state.allLicenses = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllLicensesByOrgID.fulfilled, (state, action) => {
        state.allLicenses = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(getAllLicensesByOrgID.rejected, (state, action) => {
        state.allLicenses = null;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteLicense.fulfilled, (state, action) => {
        state.message = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteLicenses.fulfilled, (state, action) => {
        state.message = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(requestHelp.fulfilled, (state, action) => {
        state.message = action.payload;
        state.loading = false;
        state.error = null;
      });
  },
});

export const { clearLicenses } = licenseSlice.actions;
export default licenseSlice.reducer;
